import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router-dom';
import { onboardingApi, startBillingSetup, errorMessage } from '../../utils/onboardingApi';

// Inline actions for the checklist steps that do something there and then.
// Each gets `onChanged` (re-fetch the checklist) and `clubId`/`data` as needed.

function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

export function BillingForm({ gymnasts, billing }) {
  const { busy, error, run } = useAction();
  const price = billing?.rateLabel || "Your monthly price is set when you join. Ask us if you'd like to know it first.";
  return (
    <div className="get-started__inline">
      <p className="get-started__muted">
        {price} Paid by Direct Debit or card on the 1st, for the month before. The first bill only counts from the day you set this up.
        {gymnasts?.limit != null && ` Until it's set up, the club can have up to ${gymnasts.limit} gymnasts (${gymnasts.count} so far).`}
      </p>
      <div className="get-started__actions">
        <button type="button" className="bk-btn bk-btn--primary" disabled={busy} onClick={() => run(startBillingSetup)}>
          {busy ? 'Opening Stripe…' : 'Set up Direct Debit or card'}
        </button>
      </div>
      <p className="get-started__small">Opens Stripe's secure page, then brings you back here.</p>
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </div>
  );
}

export function FamilyForm({ clubId, onChanged }) {
  const [email, setEmail] = useState('');
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [resent, setResent] = useState(false);
  const { busy, error, setError, run } = useAction();
  const { busy: resending, error: resendError, run: runResend } = useAction();

  const submit = (e) => {
    e.preventDefault();
    setNeedsConfirm(false);
    setResent(false);
    return run(async () => {
      try {
        await onboardingApi.inviteFamily(email.trim());
        setEmail('');
        onChanged();
      } catch (err) {
        if (err?.response?.status === 409 && err.response.data?.code === 'EMAIL_NOT_CONFIRMED') {
          setNeedsConfirm(true);
          return;
        }
        throw err;
      }
    });
  };

  const resend = () => runResend(async () => {
    await onboardingApi.resendEmailConfirmation(clubId);
    setResent(true);
  });

  return (
    <form className="get-started__inline" onSubmit={submit}>
      <label className="bk-label" htmlFor="gs-family-email">Parent's email address</label>
      <input
        id="gs-family-email" type="email" required className="bk-input" autoComplete="off"
        value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
      />
      <p className="get-started__small">This opens sign-ups and turns on emails from your club.</p>
      {needsConfirm && (
        <div className="get-started__notice" role="alert">
          <p>Confirm your club's email first</p>
          {resent
            ? <p className="get-started__small">Sent. Check the club's inbox, then try again.</p>
            : <button type="button" className="bk-btn bk-btn--sm" disabled={resending} onClick={resend}>Send the link again</button>}
        </div>
      )}
      {(error || resendError) && <p className="bk-form-error" role="alert">{error || resendError}</p>}
      <div className="get-started__actions">
        <button type="submit" className="bk-btn bk-btn--primary" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
      </div>
    </form>
  );
}

export function CoachForm({ onChanged }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [notSent, setNotSent] = useState(false);
  const { busy, error, setError, run } = useAction();
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(''); };

  const submit = (e) => {
    e.preventDefault();
    return run(async () => {
      const res = await onboardingApi.addCoach({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() });
      // Refreshing marks the step done and closes it, so a message to read
      // waits for OK first
      if (res?.data?.emailSent === false) setNotSent(true);
      else onChanged();
    });
  };

  if (notSent) {
    return (
      <div className="get-started__inline">
        <div className="get-started__notice" role="status">
          <p>
            Coach added. Their invite email wasn't sent because emails are off for your club. Turn on email sending
            in <Link to="/club-settings">Club settings</Link>, then use Send password reset on their profile to send it.
          </p>
        </div>
        <div className="get-started__actions">
          <button type="button" className="bk-btn bk-btn--primary" onClick={() => { setNotSent(false); onChanged(); }}>OK</button>
        </div>
      </div>
    );
  }

  return (
    <form className="get-started__inline" onSubmit={submit}>
      <label className="bk-label" htmlFor="gs-coach-first">First name</label>
      <input id="gs-coach-first" className="bk-input" required autoComplete="off" value={form.firstName} onChange={set('firstName')} />
      <label className="bk-label" htmlFor="gs-coach-last">Last name</label>
      <input id="gs-coach-last" className="bk-input" required autoComplete="off" value={form.lastName} onChange={set('lastName')} />
      <label className="bk-label" htmlFor="gs-coach-email">Email address</label>
      <input id="gs-coach-email" className="bk-input" type="email" required autoComplete="off" value={form.email} onChange={set('email')} />
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      <div className="get-started__actions">
        <button type="submit" className="bk-btn bk-btn--primary" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
      </div>
    </form>
  );
}

export function ShareForm({ joinUrl, onChanged }) {
  const [copied, setCopied] = useState(false);
  const { busy, error, run } = useAction();

  if (!joinUrl) {
    return (
      <div className="get-started__inline">
        <p className="get-started__muted">The club needs a web address name before it has a sign-up link.</p>
        <div className="get-started__actions"><Link className="bk-btn bk-btn--primary" to="/club-settings">Go to Club settings</Link></div>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="get-started__inline">
      <div className="get-started__share">
        <QRCodeSVG value={joinUrl} size={88} marginSize={1} title="QR code for the club's sign-up link" />
        <div className="get-started__share-side">
          <code>{joinUrl}</code>
          <div className="get-started__actions">
            <button type="button" className="bk-btn" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
            <button
              type="button" className="bk-btn bk-btn--primary" disabled={busy}
              onClick={() => run(async () => { await onboardingApi.shared(); onChanged(); })}
            >
              I've shared it
            </button>
          </div>
        </div>
      </div>
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </div>
  );
}

export function EmailForm({ club, clubId }) {
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useAction();

  if (!club?.email) {
    return (
      <div className="get-started__inline">
        <p className="get-started__muted">The club has no email address yet.</p>
        <div className="get-started__actions"><Link className="bk-btn bk-btn--primary" to="/club-settings">Go to Club settings</Link></div>
      </div>
    );
  }
  return (
    <div className="get-started__inline">
      <p className="get-started__muted">We'll send a link to <strong>{club.email}</strong>.</p>
      {sent
        ? <p className="get-started__small">Sent. Open the link in that inbox and this will tick off.</p>
        : (
          <div className="get-started__actions">
            <button
              type="button" className="bk-btn bk-btn--primary" disabled={busy}
              onClick={() => run(async () => { await onboardingApi.resendEmailConfirmation(clubId); setSent(true); })}
            >
              Send the link again
            </button>
          </div>
        )}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </div>
  );
}
